import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import StepIndicator from "./StepIndicator";
import AccountCreationStep from "./AccountCreationStep";
import OrderFormStep from "./OrderFormStep";
import RequiredChecksStep from "./RequiredChecksStep";
import { toast } from "@/hooks/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { supabase } from "@/integrations/supabase/client";
import HCaptcha from "@hcaptcha/react-hcaptcha";

const OrderWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isStep2Valid, setIsStep2Valid] = useState(false);
  const [isStep3Valid, setIsStep3Valid] = useState(false);
  const [step3Data, setStep3Data] = useState({
    animalTermsAgreed: false,
    lmoTermsAgreed: false,
    orderCautionAgreed: false,
    privacyAgreed: false,
  });
  const totalSteps = 3;
  
  // Captcha state
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaExpired, setCaptchaExpired] = useState(false);

  // Rate limiting state for order forms
  const [lastOrderSubmissionTime, setLastOrderSubmissionTime] = useState<number>(0);
  const [orderSubmissionCount, setOrderSubmissionCount] = useState<number>(0);
  const [isOrderRateLimited, setIsOrderRateLimited] = useState<boolean>(false);
  const [orderRateLimitMessage, setOrderRateLimitMessage] = useState<string>('');

  // Load rate limiting state from localStorage
  useEffect(() => {
    const lastOrderSubmission = localStorage.getItem('lastOrderSubmission');
    const orderSubmissionCountStr = localStorage.getItem('orderSubmissionCount');
    
    if (lastOrderSubmission) {
      const timeSinceLastOrderSubmission = Date.now() - parseInt(lastOrderSubmission);
      const minutesSinceLastOrderSubmission = timeSinceLastOrderSubmission / (1000 * 60);
      
      if (minutesSinceLastOrderSubmission < 30) {
        const count = parseInt(orderSubmissionCountStr || '0');
        setOrderSubmissionCount(count);
        setLastOrderSubmissionTime(parseInt(lastOrderSubmission));
        
        if (count >= 2) {
          setIsOrderRateLimited(true);
          setOrderRateLimitMessage(`주문 제출이 제한되었습니다. ${Math.ceil(30 - minutesSinceLastOrderSubmission)}분 후에 다시 시도해주세요.`);
        }
      } else {
        // Reset count after 30 minutes
        setOrderSubmissionCount(0);
        setIsOrderRateLimited(false);
        localStorage.removeItem('orderSubmissionCount');
      }
    }
  }, []);

  const handleNext = () => {
    if (currentStep === 2 && !isStep2Valid) {
      const getMissingFields = (window as any).getStep2MissingFields;
      if (getMissingFields) {
        const missingFields = getMissingFields();
        if (missingFields.length > 0) {
          toast({
            title: "필수 항목이 누락되었습니다",
            description: `다음 항목을 입력해주세요: ${missingFields.join(", ")}`,
            variant: "destructive",
          });
          return;
        }
      }
      toast({
        title: "오류",
        description: "모든 필수 항목을 입력해주세요.",
        variant: "destructive",
      });
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    console.log('Order handleSubmit called');
    console.log('captchaToken:', captchaToken ? 'Present' : 'Missing');
    console.log('isStep3Valid:', isStep3Valid);
    
    if (!isStep3Valid) {
      toast({
        title: "약관 동의 필요",
        description: "모든 필수 약관에 동의해주세요.",
        variant: "destructive",
      });
      return;
    }

    // Rate limiting check for order forms
    if (isOrderRateLimited) {
      toast({
        title: "주문 제출 제한",
        description: orderRateLimitMessage,
        variant: "destructive",
      });
      return;
    }
    
         // Captcha validation
     if (!captchaToken) {
       toast({
         title: "보안 확인 필요",
         description: "로봇이 아님을 확인해주세요.",
         variant: "destructive",
       });
       return;
     }

    // Client-side rate limiting (2 order submissions per hour - stricter than contact)
    const now = Date.now();
    const timeSinceLastOrderSubmission = now - lastOrderSubmissionTime;
    const minutesSinceLastOrderSubmission = timeSinceLastOrderSubmission / (1000 * 60);

    if (minutesSinceLastOrderSubmission < 30 && orderSubmissionCount >= 2) {
      const waitMinutes = Math.ceil(30 - minutesSinceLastOrderSubmission);
      setIsOrderRateLimited(true);
      setOrderRateLimitMessage(`주문 제출이 너무 빈번합니다. ${waitMinutes}분 후에 다시 시도해주세요.`);
      toast({
        title: "주문 제출 제한",
        description: `주문 제출이 너무 빈번합니다. ${waitMinutes}분 후에 다시 시도해주세요.`,
        variant: "destructive",
      });
      return;
    }
    
    // Get order data from Step 2
    const getOrderData = (window as any).getOrderData;
    if (!getOrderData) {
      toast({
        title: "오류",
        description: "주문 정보를 가져올 수 없습니다. 페이지를 새로고침 후 다시 시도해주세요.",
        variant: "destructive",
      });
      return;
    }
    
    const orderData = getOrderData();
    
    try {
      // Validate critical fields before submission
      if (!orderData.institutionType || orderData.institutionType.trim() === "") {
        throw new Error("기관 유형을 선택해주세요.");
      }
      if (!orderData.partialShipment || orderData.partialShipment.trim() === "") {
        throw new Error("부분 선적 허용여부를 선택해주세요.");
      }
      if (!['profit', 'non-profit'].includes(orderData.institutionType)) {
        throw new Error("올바른 기관 유형을 선택해주세요.");
      }
      if (!['yes', 'no'].includes(orderData.partialShipment)) {
        throw new Error("부분 선적 허용여부를 선택해주세요.");
      }
      
      // Send order notification email only (no database storage)

      // Update rate limiting tracking for orders
      const now = Date.now();
      const newOrderCount = orderSubmissionCount + 1;
      setLastOrderSubmissionTime(now);
      setOrderSubmissionCount(newOrderCount);
      localStorage.setItem('lastOrderSubmission', now.toString());
      localStorage.setItem('orderSubmissionCount', newOrderCount.toString());
      
      // Check if user should be rate limited (2 orders per 30 minutes)
      if (newOrderCount >= 2) {
        setIsOrderRateLimited(true);
        setOrderRateLimitMessage('30분 내 최대 2회까지 주문 제출 가능합니다. 잠시 후 다시 시도해주세요.');
      }

      try {
        
        const { data, error: emailError } = await supabase.functions.invoke('send-order-notification', {
          body: {
            ...orderData,
            step3Agreements: step3Data,
            captcha_token: captchaToken
          }
        });
        

        
        if (emailError) {

          toast({
            title: "주문 제출 실패",
            description: `주문 정보 전송에 실패했습니다: ${emailError.message || '알 수 없는 오류'}`,
            variant: "destructive",
          });
          return;
        }
        
        // Success - email notification sent
        toast({
          title: "주문 완료",
          description: "주문이 성공적으로 제출되었습니다. 담당자가 별도로 연락드릴 예정입니다.",
        });
        
        // Reset captcha token
        setCaptchaToken(null);
        
      } catch (emailError) {
        toast({
          title: "주문 제출 실패",
          description: "주문 정보 전송에 실패했습니다. 다시 시도해주세요.",
          variant: "destructive",
        });
        return;
      }
      
    } catch (error) {
      toast({
        title: "주문 제출 실패",
        description: error instanceof Error ? error.message : "주문 제출 중 오류가 발생했습니다. 다시 시도해주세요.",
        variant: "destructive",
      });
    }
  };

  const handleStep2ValidationChange = (isValid: boolean) => {
    setIsStep2Valid(isValid);
  };

  const handleStep3ValidationChange = (isValid: boolean, agreementData?: any) => {
    setIsStep3Valid(isValid);
    if (agreementData) {
      setStep3Data(agreementData);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <AccountCreationStep />;
      case 2:
        return <OrderFormStep onValidChange={handleStep2ValidationChange} />;
      case 3:
        return <RequiredChecksStep onValidChange={handleStep3ValidationChange} />;
      default:
        return <AccountCreationStep />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-20 max-w-5xl">
      {/* Header */}
      <div className="text-center mb-8 md:mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          주문방법
        </h2>
        <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto mb-6 md:mb-8 px-2">
          단계별 주문 과정을 통해 쉽고 빠르게 주문하세요.
        </p>
        <div className="flex justify-center">
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
        </div>
      </div>

      {/* Mobile Desktop Recommendation */}
      <div className="mb-6 md:hidden">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-center">
          <p className="text-blue-800 text-sm">
            정확한 정보 기입과 원활한 사용자 경험을 위해 PC에서 주문서 작성을 추천합니다.
          </p>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-background border rounded-lg p-4 md:p-8 mb-6 md:mb-8 min-h-[400px] md:min-h-[600px]">
        {renderStep()}
      </div>

      {/* Captcha and Warnings for Final Step */}
      {currentStep === totalSteps && (
        <div className="flex flex-col items-center gap-3 mb-6">
          {/* Captcha */}
          <div className="flex justify-center">
            <HCaptcha
              key="order-form-captcha"
              sitekey="4296bc82-6fde-420e-8fec-4da0436405be"
              onVerify={(token) => {
                console.log('Order HCaptcha verified:', token ? 'Token received' : 'No token');
                setCaptchaToken(token);
                setCaptchaExpired(false);
              }}
              onExpire={() => {
                console.log('Order HCaptcha expired');
                setCaptchaToken(null);
                setCaptchaExpired(true);
              }}
              onError={() => {
                console.log('Order HCaptcha error');
                setCaptchaToken(null);
                setCaptchaExpired(true);
              }}
            />
          </div>
          
          {captchaExpired && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg max-w-md">
              <p className="text-sm text-red-800 font-medium text-center">⚠️ 보안 인증이 만료되었습니다. 다시 인증해주세요.</p>
            </div>
          )}
          
          {isOrderRateLimited && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg max-w-md">
              <p className="text-sm text-yellow-800 font-medium text-center">⚠️ {orderRateLimitMessage}</p>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className="flex flex-col-reverse md:flex-row gap-4 md:justify-between md:items-center">
        <div className="flex justify-center md:justify-start">
          {currentStep > 1 && (
            <Button variant="outline" onClick={handlePrevious} size="lg" className="w-full md:w-auto">
              이전
            </Button>
          )}
        </div>
        
        <div className="flex justify-center md:justify-end">
          {currentStep < totalSteps ? (
            <Button onClick={handleNext} size="lg" className="w-full md:w-auto px-4 md:px-8">
              <span className="hidden md:inline">
                {currentStep === 1 ? "다음 단계: 주문 정보 입력" : currentStep === 2 ? "다음 단계: 필수 약관 확인" : `다음: Step ${currentStep + 1}/${totalSteps} ${getNextStepTitle()}`}
              </span>
              <span className="md:hidden">
                {currentStep === 1 ? "주문 정보 입력" : currentStep === 2 ? "필수 약관 확인" : "다음 단계"}
              </span>
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              size="lg" 
              className="w-full md:w-auto"
              disabled={!isStep3Valid || isOrderRateLimited || !captchaToken || captchaExpired}
            >
              {isOrderRateLimited ? "제출 제한됨" : "주문하기"}
            </Button>
          )}
        </div>
      </div>

      {currentStep === totalSteps && (
        <div className="text-center mt-4">
          <p className="text-sm text-muted-foreground">
            주문 확인 후, 담당자가 별도로 연락드릴 예정입니다.
          </p>
        </div>
      )}
      <Toaster />
    </div>
  );
};

const getNextStepTitle = () => {
  const titles = {
    2: "주문 정보 입력",
    3: "필수 체크 표시"
  };
  return titles[2] || "";
};

export default OrderWizard;